import TextField from '@brands/Components/Inputs/MuiTextField/TextField';
import Sidenav from '@brands/Dashboard/Sidenav/Sidenav';
import { useWindowSize } from '@brands/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import React, { FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import Modal from 'react-modal';
import * as Yup from 'yup';

import Button from '../../Components/Button/Button';
import { requestEmailUpdate } from '../../services/auth/requestEmailUpdate';
import { customStylesForgotPassword } from '../../Utils/customStyles';
import { displayErrorDetails } from '../../Utils/displayError';
import ContactCustomerServices from './Components/ContactCustomerServices';
import styles from './styles.module.scss';

interface UpdateEmailModalStepOneProps {
  updateEmailStep: number;
  setUpdateEmailStep: (num: number) => void;
  setCredentials: (credentials: UpdateEmailCredentials) => void;
}

type UpdateEmailCredentials = {
  username?: string;
  code: string;
  new_password: string;
  reset_password_hash?: string;
  last_four_digits?: string;
};

type UpdateEmailModalStepOneSubmitForm = {
  email?: string;
  phoneNumber?: string;
};

const validationSchema = Yup.object().shape({
  email: Yup.string()
    .required('Email is required')
    .test('test-name', 'Enter Valid Email', (value: string | undefined) => {
      const emailRegex =
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      const phoneRegex = /^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;
      const isValidEmail = emailRegex.test(value as string);
      const isValidPhone = phoneRegex.test(value as string);
      if (!isValidEmail && !isValidPhone) {
        return false;
      }
      return true;
    }),
});

const UpdateEmailModalStepOne: FC<UpdateEmailModalStepOneProps> = ({
  updateEmailStep,
  setUpdateEmailStep,
  setCredentials,
}) => {
  const [loading, setLoading] = useState(false);
  const isMobile = useWindowSize().width < 992;
  const [contactCustomerSupport, setContactCustomerSupport] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<UpdateEmailModalStepOneSubmitForm>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
  });

  const closeModal = (): void => {
    setUpdateEmailStep(0);
    setCredentials({
      username: '',
      code: '',
      new_password: '',
      reset_password_hash: '',
      last_four_digits: '',
    });
  };

  const onSubmit = async (data: UpdateEmailModalStepOneSubmitForm): Promise<void> => {
    try {
      setLoading(true);

      const email = data.email?.toLowerCase() as string;
      await requestEmailUpdate({
        current_email: email,
      }).then((response) => {
        if (response.requires_manual_verification) {
          setContactCustomerSupport(true);
        } else {
          setUpdateEmailStep(2);
          setCredentials({
            username: email,
            code: '',
            new_password: '',
            reset_password_hash: '',
            last_four_digits: response.last_four_digits_of_phone || '',
          });
        }
      });
    } catch (error) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (updateEmailStep === 1) {
      document.body.classList.add('modal-open');
    } else if (document.body.classList.contains('modal-open') && updateEmailStep === 0) {
      document.body.classList.remove('modal-open');
    }
  }, [updateEmailStep]);
  return (
    <div className={styles.container}>
      <Sidenav isVisibleOnMobile hideDefaultOptions authLoog hideModals={() => closeModal()} />
      <Modal
        isOpen={updateEmailStep === 1}
        onRequestClose={closeModal}
        style={customStylesForgotPassword(isMobile, '40%')}
        contentLabel="Modal"
        ariaHideApp={false}
        className={`${styles.wrapper} ${styles.forgotPasswordModal}`}
        shouldCloseOnOverlayClick={false}
      >
        {contactCustomerSupport ? (
          <ContactCustomerServices />
        ) : (
          <form className={styles.formContainer} onSubmit={handleSubmit(onSubmit)}>
            <span className={`${styles.formTitle} p-b-26`}>
              <span className={`${styles.formTitleForgot} fs-unmask`}>Update Email Address</span>
              <div className={`text-center ${styles.formTitleLink} p-t-115 fs-unmask`}>
                <span className={`${styles.txt1} txt1`}>Enter your current email address.</span>
              </div>
            </span>
            <div className={styles.inputWrapper} data-validate="Valid email is: a@b.c">
              <TextField
                {...register('email')}
                label="Current Email"
                errors={errors.email ? errors : {}}
                name="email"
                onChange={(e) => {
                  setValue('email', e.target.value, { shouldValidate: true });
                }}
                inputClass={`${styles.profileInputClass} fs-exclude`}
                wrapperClassName={`fs-unmask ${styles.createAccountClass}`}
              />
            </div>
            <div className={styles.buttonContainer}>
              <div className={styles.buttonWrapper}>
                <Button type="submit" className={styles.formBtn} disabled={loading}>
                  Continue
                </Button>
              </div>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

Modal.setAppElement('#root');

export default UpdateEmailModalStepOne;
